import React, { useRef } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { Fade } from "@mui/material"
import { DISCARD_IMAGE_CROP, SAVE_CROPPED_IMAGE } from "constants/events"
import { CROP_SHAPE } from "types/general.types"
import CTA from "components/atoms/CTA"
import ImageCropper from "components/atoms/ImageCropper"

type ImageCropperModalProps = {
  show?: boolean;
  src: string | null;
  aspectRatio?: number;
  cropShape?: CROP_SHAPE;
  loading?: boolean;
}

export default function ImageCropperModal(props: ImageCropperModalProps) {
  const {
    show = true,
    src,
    aspectRatio = 1,
    cropShape = "round",
    loading = false
  } = props

  const cropperRef = useRef<{ handleSave: () => void, handleDiscard: () => void } | null>(null)

  const { t } = useTranslation()

  function handleSubmit() {
    cropperRef.current?.handleSave()
  }

  function handleDiscard() {
    cropperRef.current?.handleDiscard()
  }

  function onSave(imageUrl: string, blob: Blob) {
    document.dispatchEvent(new CustomEvent(SAVE_CROPPED_IMAGE, { detail: { url: imageUrl, blob } }))
  }

  function onDiscard() {
    document.dispatchEvent(new CustomEvent(DISCARD_IMAGE_CROP))
  }

  return (
    createPortal(
      <Fade
        id="image-cropper-modal"
        in={show}
        easing={{
          enter: "ease-in-out",
          exit: "linear"
        }}
        timeout={0}
        style={{ position: "fixed", top: 0, left: 0 }}
      >
        <Container>
          <ButtonsContainer>
            <div>
              <CTA
                variant="text"
                color="inherit"
                loading={loading}
                onClick={handleSubmit}
              >
                {t("general.submit")}
              </CTA>
            </div>
            <div>
              <CTA
                variant="text"
                color="inherit"
                onClick={handleDiscard}
              >
                {t("general.discard")}
              </CTA>
            </div>
          </ButtonsContainer>
          {show && <ImageCropper
              ref={cropperRef}
              src={src || ""}
              options={{
                cropShape,
                aspectRatio,
                initialAspectRatio: aspectRatio
              }}
              onSave={onSave}
              onDiscard={onDiscard}
          />}
        </Container>
      </Fade>,
      document.body
    )
  )
}

const Container = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  justifyContent: "flex-start",
  alignItems: "flex-end",
  backgroundColor: theme.palette.gray["16"],
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 101,

  "& svg": {
    stroke: "#FFFFFF !important"
  },

  "& img": {
    objectFit: "contain"
  },

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const ButtonsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  width: "100%",
  justifyContent: "space-between",
  backgroundColor: theme.palette.gray["16"],
  zIndex: 2
}))
